import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@soroush/ui';
import { apiFetch } from '../lib/api';
import { formatFlightDate, formatFlightTime, formatMoney } from '../lib/format';
import { cityName } from '../lib/airports';
import { toFaDigits } from '../lib/jalali';
import { BookingBar } from '../components/BookingBar';
import { FareDateStrip, SearchResultBar, lowestBookableFare } from '../components/FareDateStrip';
import { FlightResultsSkeleton } from '../components/FlightResultsSkeleton';
import { BookingSteps } from '../components/BookingSteps';
import {
  departsAfter,
  offerState,
  readTripSelection,
  saveTripSelection,
} from '../lib/roundTrip';

interface SearchResult {
  flightId: string;
  flightNumber: string;
  origin: string;
  destination: string;
  departureAt: string;
  arrivalAt: string | null;
  cabin: string;
  seatsAvailable: number | null;
  totalFare: number | null;
  currency: string;
  baseFare?: number | null;
  taxes?: number | null;
  flightStatus?: string;
  bookable?: boolean;
  source?: 'nrs' | 'local';
  fareClass?: string;
  refundable?: boolean | null;
  aircraft?: string;
  /** Every open booking class in the cabin, cheapest first (live NRS sales only). */
  fareOptions?: FareOption[];
}

interface FareOption {
  fareClass: string;
  totalFare: number | null;
  seatsAvailable: number | null;
  refundable: boolean | null;
}

export function SearchPage() {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language.startsWith('fa');
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const searchId = (location.state as { searchId?: number } | null)?.searchId ?? 0;
  const [results, setResults] = useState<SearchResult[]>([]);
  const [returnResults, setReturnResults] = useState<SearchResult[]>([]);
  const [error, setError] = useState('');
  const [returnError, setReturnError] = useState('');
  const [loading, setLoading] = useState(true);
  const [niraMsg, setNiraMsg] = useState('');
  const [leg, setLeg] = useState<'outbound' | 'return'>('outbound');
  const [outbound, setOutbound] = useState<SearchResult | null>(null);
  const [returnFlight, setReturnFlight] = useState<SearchResult | null>(null);
  const [hint, setHint] = useState('');
  const [multiResults, setMultiResults] = useState<SearchResult[][]>([]);
  const [multiChosen, setMultiChosen] = useState<(SearchResult | null)[]>([]);
  const [activeLeg, setActiveLeg] = useState(0);
  const [editing, setEditing] = useState(false);

  const query = useMemo(() => {
    const departureDate =
      params.get('departureDate') ?? new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    const returnDate = params.get('returnDate') ?? '';
    return {
      origin: params.get('origin') ?? 'IKA',
      destination: params.get('destination') ?? 'MHD',
      departureDate,
      returnDate: returnDate >= departureDate ? returnDate : '',
      cabin: params.get('cabin') ?? 'economy',
      passengers: params.get('passengers') ?? '1',
      children: params.get('children') ?? '0',
      infants: params.get('infants') ?? '0',
    };
  }, [params]);

  const multiLegs = useMemo(() => {
    if (params.get('trip') !== 'multi') return [];
    return params.getAll('leg').flatMap((raw) => {
      const [origin, destination, departureDate] = raw.split('.');
      if (!origin || !destination || !departureDate) return [];
      return [{ origin, destination, departureDate }];
    });
  }, [params]);
  const multi = multiLegs.length >= 2;
  const roundTrip = Boolean(query.returnDate) && !multi;
  const selectionKey = [
    query.origin,
    query.destination,
    query.departureDate,
    query.returnDate,
    query.cabin,
    query.passengers,
    query.children,
    query.infants,
  ].join('|');

  useEffect(() => {
    if (!roundTrip) {
      setLeg('outbound');
      setOutbound(null);
      setReturnFlight(null);
      setHint('');
      return;
    }
    const saved = readTripSelection<SearchResult>(selectionKey);
    setOutbound(saved?.outbound ?? null);
    setReturnFlight(saved?.returnFlight ?? null);
    setLeg(saved?.outbound ? 'return' : 'outbound');
    setHint('');
  }, [roundTrip, selectionKey]);

  useEffect(() => {
    setEditing(false);
  }, [searchId]);

  useEffect(() => {
    if (!roundTrip || (!outbound && !returnFlight)) return;
    saveTripSelection({ key: selectionKey, outbound, returnFlight });
  }, [roundTrip, selectionKey, outbound, returnFlight]);

  useEffect(() => {
    if (params.get('trip') === 'multi') return;
    let cancelled = false;
    const started = performance.now();
    setLoading(true);
    setResults([]);
    setReturnResults([]);
    setError('');
    setReturnError('');
    const outboundQs = new URLSearchParams({
      origin: query.origin,
      destination: query.destination,
      departureDate: query.departureDate,
      cabin: query.cabin,
      passengers: query.passengers,
    });
    const returnQs = new URLSearchParams({
      origin: query.destination,
      destination: query.origin,
      departureDate: query.returnDate,
      cabin: query.cabin,
      passengers: query.passengers,
    });
    for (const qs of [outboundQs, returnQs]) {
      if (query.children !== '0') qs.set('children', query.children);
      if (query.infants !== '0') qs.set('infants', query.infants);
    }
    const load = (qs: URLSearchParams) =>
      apiFetch<{
        results: SearchResult[];
        nira?: { message?: string; degraded?: boolean };
      }>(`/api/v1/search?${qs}`);

    const settled = (qs: URLSearchParams) =>
      load(qs).then(
        (data) => ({ ok: true as const, data }),
        (err: Error) => ({ ok: false as const, message: err.message || t('common.errorGeneric') }),
      );

    void Promise.all([settled(outboundQs), roundTrip ? settled(returnQs) : Promise.resolve(null)])
      .then(([outboundResult, returnResult]) => {
        if (cancelled) return;
        if (outboundResult.ok) {
          setResults(outboundResult.data.results);
        } else {
          setResults([]);
          setError(outboundResult.message);
        }
        if (returnResult?.ok) {
          setReturnResults(returnResult.data.results);
        } else if (returnResult) {
          setReturnResults([]);
          setReturnError(returnResult.message);
        }
        const degraded = [outboundResult, returnResult]
          .map((result) => (result?.ok ? result.data.nira : undefined))
          .find((nira) => nira?.degraded);
        setNiraMsg(degraded?.message ?? '');
      })
      .finally(() => {
        void holdResultsSkeleton(started).then(() => {
          if (!cancelled) setLoading(false);
        });
      });
    return () => {
      cancelled = true;
    };
  }, [query, roundTrip, t, params, searchId]);

  const multiKey = multiLegs.map((leg) => `${leg.origin}.${leg.destination}.${leg.departureDate}`).join('|');

  useEffect(() => {
    if (!multi) return;
    let cancelled = false;
    const started = performance.now();
    setLoading(true);
    setMultiResults([]);
    setError('');
    setActiveLeg(0);
    setMultiChosen(multiLegs.map(() => null));
    const requests = multiLegs.map((leg) => {
      const qs = new URLSearchParams({
        origin: leg.origin,
        destination: leg.destination,
        departureDate: leg.departureDate,
        cabin: query.cabin,
        passengers: query.passengers,
      });
      if (query.children !== '0') qs.set('children', query.children);
      if (query.infants !== '0') qs.set('infants', query.infants);
      return apiFetch<{ results: SearchResult[] }>(`/api/v1/search?${qs}`).then(
        (data) => data.results,
        () => [] as SearchResult[],
      );
    });
    void Promise.all(requests)
      .then((groups) => {
        if (!cancelled) setMultiResults(groups);
      })
      .finally(() => {
        void holdResultsSkeleton(started).then(() => {
          if (!cancelled) setLoading(false);
        });
      });
    return () => {
      cancelled = true;
    };
  }, [multi, multiKey, multiLegs, query.cabin, query.passengers, query.children, query.infants, searchId]);

  const visibleReturns = useMemo(() => {
    if (!outbound) return returnResults;
    const earliest = outbound.arrivalAt ?? outbound.departureAt;
    return returnResults.filter((flight) => departsAfter(flight.departureAt, earliest));
  }, [outbound, returnResults]);

  const showingReturn = roundTrip && leg === 'return';
  const visible = showingReturn ? visibleReturns : results;
  const activeError = showingReturn ? returnError : error;
  const emptyMessage = showingReturn
    ? returnResults.length > 0 && visibleReturns.length === 0
      ? t('common.returnTooEarly')
      : t('common.noReturnResults')
    : t('common.noResults');

  const stripRoute = multi
    ? multiLegs[activeLeg]
    : showingReturn
      ? { origin: query.destination, destination: query.origin, departureDate: query.returnDate }
      : { origin: query.origin, destination: query.destination, departureDate: query.departureDate };
  const stripFlights = multi ? (multiResults[activeLeg] ?? []) : showingReturn ? returnResults : results;
  const knownFare = lowestBookableFare(stripFlights);

  function selectStripDate(iso: string) {
    const next = new URLSearchParams(params);
    if (multi) {
      const legs = next.getAll('leg');
      next.delete('leg');
      legs.forEach((leg, index) => {
        if (index !== activeLeg) {
          next.append('leg', leg);
          return;
        }
        const [from, to] = leg.split('.');
        next.append('leg', `${from}.${to}.${iso}`);
      });
    } else if (showingReturn) {
      next.set('returnDate', iso);
    } else {
      next.set('departureDate', iso);
      const ret = next.get('returnDate');
      if (ret && ret < iso) next.delete('returnDate');
    }
    setEditing(false);
    setParams(next);
  }

  const cabinOptions = ['economy', 'business', query.cabin].filter(
    (cabin, index, all) => all.indexOf(cabin) === index,
  );
  const shownFlights = multi ? (multiResults[activeLeg] ?? []) : visible;

  function chooseMulti(chosen: SearchResult) {
    const next = multiChosen.slice();
    next[activeLeg] = chosen;
    setMultiChosen(next);
    if (activeLeg < multiLegs.length - 1) {
      setActiveLeg(activeLeg + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const flights = next.filter((flight): flight is SearchResult => Boolean(flight));
    if (flights.length !== multiLegs.length) return;
    const [first, second, ...rest] = flights;
    navigate('/book', {
      state: {
        flight: first,
        returnFlight: second,
        extraFlights: rest,
        passengers: Number(query.passengers) || 1,
      },
    });
  }

  return (
    <main className="sa-page">
      <div className="sa-container">
        <BookingSteps current={0} />
        <div className="sa-page__header">
          <p className="home-section__eyebrow">{t('nav.items.buyTicket')}</p>
          <h1 className="sa-headline">
            {roundTrip
              ? showingReturn
                ? t('common.selectReturn')
                : t('common.selectOutbound')
              : t('common.findFlight')}
          </h1>
        </div>

        {editing ? (
          <div id="search-editor" style={{ marginBottom: '1.15rem' }}>
            <BookingBar pending={loading} />
          </div>
        ) : null}

        {stripRoute ? (
          <>
            <SearchResultBar
              origin={stripRoute.origin}
              destination={stripRoute.destination}
              date={stripRoute.departureDate}
              passengers={Number(query.passengers) || 1}
              editing={editing}
              onEdit={() => setEditing((open) => !open)}
            />
            <FareDateStrip
              origin={stripRoute.origin}
              destination={stripRoute.destination}
              selected={stripRoute.departureDate}
              cabin={query.cabin}
              passengers={query.passengers}
              children={query.children}
              infants={query.infants}
              currency={stripFlights.find((flight) => flight.currency)?.currency ?? 'IRR'}
              knownFare={knownFare}
              knownReady={!loading}
              onSelect={selectStripDate}
            />
          </>
        ) : null}

        <div className="cabin-switch" role="tablist" aria-label={t('common.cabinClass')}>
          {cabinOptions.map((cabin) => (
            <button
              key={cabin}
              type="button"
              role="tab"
              aria-selected={query.cabin === cabin}
              className={query.cabin === cabin ? 'is-active' : ''}
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set('cabin', cabin);
                setParams(next);
              }}
            >
              {t(`common.${cabin}`, { defaultValue: cabin })}
            </button>
          ))}
        </div>

        {multi ? (
          <div className="trip-legs" role="tablist" aria-label={t('common.multiCity')}>
            {multiLegs.map((leg, index) => (
              <button
                key={`${leg.origin}-${leg.destination}-${index}`}
                type="button"
                role="tab"
                aria-selected={activeLeg === index}
                className={`trip-legs__tab${activeLeg === index ? ' is-active' : ''}${
                  multiChosen[index] ? ' is-chosen' : ''
                }`}
                onClick={() => setActiveLeg(index)}
              >
                <span>
                  {t('common.flightN', { n: isFa ? toFaDigits(index + 1) : index + 1 })}
                </span>
                <span>
                  <bdi>{`${leg.origin} → ${leg.destination}`}</bdi>
                </span>
              </button>
            ))}
          </div>
        ) : null}

        {roundTrip ? (
          <div className="trip-legs" role="tablist" aria-label={t('common.roundTrip')}>
            <button
              type="button"
              role="tab"
              aria-selected={!showingReturn}
              className={`trip-legs__tab${!showingReturn ? ' is-active' : ''}${outbound ? ' is-chosen' : ''}`}
              onClick={() => setLeg('outbound')}
            >
              <span>{t('common.outboundFlight')}</span>
              <span>
                {outbound ? (
                  <bdi>{`${outbound.origin} → ${outbound.destination}`}</bdi>
                ) : (
                  formatFlightDate(`${query.departureDate}T12:00:00.000Z`, isFa)
                )}
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={showingReturn}
              className={`trip-legs__tab${showingReturn ? ' is-active' : ''}${returnFlight ? ' is-chosen' : ''}`}
              onClick={() => setLeg('return')}
            >
              <span>{t('common.returnFlight')}</span>
              <span>
                {returnFlight ? (
                  <bdi>{`${returnFlight.origin} → ${returnFlight.destination}`}</bdi>
                ) : (
                  formatFlightDate(`${query.returnDate}T12:00:00.000Z`, isFa)
                )}
              </span>
            </button>
          </div>
        ) : null}

        {showingReturn && outbound ? (
          <SelectedLeg
            label={t('common.outboundFlight')}
            flight={outbound}
            onChange={() => setLeg('outbound')}
          />
        ) : null}
        {!showingReturn && returnFlight ? (
          <SelectedLeg
            label={t('common.returnFlight')}
            flight={returnFlight}
            onChange={() => setLeg('return')}
          />
        ) : null}

        {loading ? <FlightResultsSkeleton /> : null}
        {hint ? <div className="sa-alert">{hint}</div> : null}
        {activeError ? <div className="sa-alert sa-alert--error">{activeError}</div> : null}
        {niraMsg ? <div className="sa-alert">{niraMsg}</div> : null}

        {!loading && shownFlights.length === 0 && !activeError ? (
          <p className="sa-text-muted">{emptyMessage}</p>
        ) : null}

        {!loading ? (
        <div className="flight-results">
          {(multi ? shownFlights : visible).map((flight) => (
            <FlightResultCard
              key={flight.flightId}
              flight={flight}
              selected={
                multi
                  ? multiChosen[activeLeg]?.flightId === flight.flightId
                  : (showingReturn ? returnFlight?.flightId : outbound?.flightId) === flight.flightId
              }
              actionLabel={
                multi
                  ? activeLeg < multiLegs.length - 1
                    ? t('common.continue')
                    : t('common.selectFlight')
                  : roundTrip && (showingReturn ? outbound : returnFlight)
                    ? t('common.continue')
                    : undefined
              }
              onSelect={(chosen) => {
                if (multi) {
                  chooseMulti(chosen);
                  return;
                }
                const passengers = Number(query.passengers) || 1;
                if (!roundTrip) {
                  navigate('/book', { state: { flight: chosen, passengers } });
                  return;
                }
                const book = (out: SearchResult, back: SearchResult) => {
                  navigate('/book', {
                    state: { flight: out, returnFlight: back, passengers },
                  });
                };
                if (!showingReturn) {
                  setOutbound(chosen);
                  setHint('');
                  if (
                    returnFlight &&
                    departsAfter(returnFlight.departureAt, chosen.arrivalAt ?? chosen.departureAt)
                  ) {
                    book(chosen, returnFlight);
                    return;
                  }
                  if (returnFlight) setReturnFlight(null);
                  setLeg('return');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  return;
                }
                if (!outbound) {
                  setReturnFlight(chosen);
                  setHint(t('common.pickOutboundFirst'));
                  setLeg('outbound');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  return;
                }
                setReturnFlight(chosen);
                book(outbound, chosen);
              }}
            />
          ))}
        </div>
        ) : null}
      </div>
    </main>
  );
}

function holdResultsSkeleton(started: number) {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = reduce ? 0 : Math.max(0, 560 - (performance.now() - started));
  return wait ? new Promise<void>((resolve) => window.setTimeout(resolve, wait)) : Promise.resolve();
}

const LOW_SEATS_THRESHOLD = 9;

function SelectedLeg({
  label,
  flight,
  onChange,
}: {
  label: string;
  flight: SearchResult;
  onChange: () => void;
}) {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language.startsWith('fa');
  const number = isFa ? toFaDigits(flight.flightNumber) : flight.flightNumber;

  return (
    <div className="trip-selected">
      <div className="trip-selected__main">
        <span className="trip-selected__label">{label}</span>
        <strong dir="ltr">
          {flight.origin} → {flight.destination}
        </strong>
        <span>
          {formatFlightDate(flight.departureAt, isFa)} · {formatFlightTime(flight.departureAt, i18n.language)}
        </span>
        <span>{t('common.flightN', { n: number })}</span>
      </div>
      <button type="button" className="trip-selected__change" onClick={onChange}>
        {t('common.changeFlight')}
      </button>
    </div>
  );
}

/** The flight as priced for one booking class; the cheapest class is what the search returned. */
function withFareOption(base: SearchResult, option: FareOption | undefined): SearchResult {
  if (!option || option.fareClass === base.fareClass) return base;
  return {
    ...base,
    fareClass: option.fareClass,
    totalFare: option.totalFare,
    seatsAvailable: option.seatsAvailable,
    refundable: option.refundable,
    // The base/tax split belongs to the class the search priced; the book page re-quotes it.
    baseFare: null,
    taxes: null,
  };
}

function FlightResultCard({
  flight: searched,
  onSelect,
  selected = false,
  actionLabel,
}: {
  flight: SearchResult;
  onSelect: (flight: SearchResult) => void;
  selected?: boolean;
  actionLabel?: string;
}) {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language.startsWith('fa');
  const digits = (value: string | number) => (isFa ? toFaDigits(String(value)) : String(value));

  const options = searched.fareOptions ?? [];
  const [pickedClass, setPickedClass] = useState(searched.fareClass ?? '');
  const flight = withFareOption(
    searched,
    options.find((option) => option.fareClass === pickedClass),
  );

  const offer = offerState(flight);
  const unavailable = offer === 'soldOut' || offer === 'closed';
  const closed = offer === 'closed';
  const lowSeats =
    flight.seatsAvailable != null && flight.seatsAvailable > 0 && flight.seatsAvailable <= LOW_SEATS_THRESHOLD;

  const durationMinutes = flight.arrivalAt
    ? Math.round((new Date(flight.arrivalAt).getTime() - new Date(flight.departureAt).getTime()) / 60000)
    : null;

  const statusBadge =
    offer === 'open' ? (
      <span className="sa-badge sa-badge--success">{t('common.flightOpen')}</span>
    ) : offer === 'closed' || offer === 'soldOut' ? (
      <span className="sa-badge sa-badge--danger">
        {offer === 'closed' ? t('common.flightClosed') : t('common.soldOut')}
      </span>
    ) : null;

  return (
    <article className={`flight-result${unavailable ? ' is-unavailable' : ''}${selected ? ' is-selected' : ''}`}>
      <div className="flight-result__main">
        <div className="flight-result__meta">
          <span className="flight-result__number">
            {t('common.flightN', { n: digits(flight.flightNumber) })}
          </span>
          <span className="flight-result__sep" aria-hidden="true" />
          <span>{t(`common.${flight.cabin}`, { defaultValue: flight.cabin })}</span>
          {flight.aircraft ? (
            <>
              <span className="flight-result__sep" aria-hidden="true" />
              <span dir="ltr">{flight.aircraft}</span>
            </>
          ) : null}
          {statusBadge}
          {flight.refundable === false ? (
            <span className="sa-badge">{t('common.nonRefundable')}</span>
          ) : flight.refundable ? (
            <span className="sa-badge">{t('common.refundable')}</span>
          ) : null}
        </div>

        <div className="flight-result__route">
          <div className="flight-result__point">
            <span className="flight-result__time">
              {formatFlightTime(flight.departureAt, i18n.language)}
            </span>
            <span className="flight-result__place">
              <strong>{flight.origin}</strong>
              {cityName(flight.origin, isFa)}
            </span>
          </div>

          <div className="flight-result__path">
            <span className="flight-result__date">{formatFlightDate(flight.departureAt, isFa)}</span>
            <div className="flight-result__line" aria-hidden="true" />
            {durationMinutes != null && durationMinutes > 0 ? (
              <span className="flight-result__duration">
                {t('common.flightDuration', {
                  h: digits(Math.floor(durationMinutes / 60)),
                  m: digits(durationMinutes % 60),
                })}
              </span>
            ) : null}
          </div>

          <div className="flight-result__point flight-result__point--end">
            <span className={`flight-result__time${flight.arrivalAt ? '' : ' is-unknown'}`}>
              {flight.arrivalAt ? formatFlightTime(flight.arrivalAt, i18n.language) : '—'}
            </span>
            <span className="flight-result__place">
              <strong>{flight.destination}</strong>
              {cityName(flight.destination, isFa)}
            </span>
          </div>
        </div>

        {options.length > 1 && !unavailable ? (
          <div className="flight-result__classes" role="radiogroup" aria-label={t('common.fareOptions')}>
            {options.map((option) => {
              const active = option.fareClass === (flight.fareClass ?? '');
              return (
                <button
                  key={option.fareClass}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`fare-chip${active ? ' is-active' : ''}`}
                  onClick={() => setPickedClass(option.fareClass)}
                >
                  <span className="fare-chip__class">
                    {t('common.fareOptionLabel', { code: option.fareClass })}
                  </span>
                  {option.totalFare != null ? (
                    <span className="fare-chip__price">
                      {formatMoney(option.totalFare, flight.currency, isFa)}
                    </span>
                  ) : null}
                  {option.refundable != null ? (
                    <span className="fare-chip__note">
                      {option.refundable ? t('common.refundable') : t('common.nonRefundable')}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <div className="flight-result__fare">
        {flight.totalFare != null ? (
          <>
            <div className="flight-result__price">
              {formatMoney(flight.totalFare, flight.currency, isFa)}
              <span className="flight-result__per">{t('common.perPassenger')}</span>
            </div>
            {flight.seatsAvailable != null && !unavailable ? (
              <span className={`flight-result__seats${lowSeats ? ' is-low' : ''}`}>
                {digits(flight.seatsAvailable)} {t('common.seatsLeft')}
              </span>
            ) : null}
            <Button
              type="button"
              variant="primary"
              className="flight-result__cta"
              disabled={unavailable}
              onClick={() => onSelect(flight)}
            >
              {unavailable
                ? closed
                  ? t('common.flightClosed')
                  : t('common.soldOut')
                : (actionLabel ?? t('common.selectFlight'))}
            </Button>
          </>
        ) : (
          <span className="sa-text-muted">{t('common.fareNotSet')}</span>
        )}
      </div>
    </article>
  );
}
